import {IPresentation} from '../incase-domain/presentation';
import {ISlide} from '../incase-domain/slide';
import {IElement} from '../incase-domain/elements';
import {IAppliedRule, IOldAppliedRule, IOldPredicate, RuleOperator, RuleResultType} from '../incase-domain/appliedRules';
import {PlayerLabel} from '../incase-domain/playerLabel';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';

/**
 * IMPORTANT: This function should be same in the player and api!
 * Maps old presentation to new presentation.
 * Whenever we update the presentation model, we need to update this function.
 * It provides a way for old presentations to be updated to the new presentation model.
 * Presentation should include slides.
 * @param presentation: IPresentation
 * @param returnSlides: boolean
 * @returns IPresentation
 */

export const convertPresentationToLatestVersion = (presentation: IPresentation, returnSlides = true): IPresentation => {
    const updatedPresentation = {...presentation};

    updatedPresentation.languageConfig = mapOldLanguageConfigToNew(updatedPresentation.languageConfig);

    if (!presentation.appliedRules?.length) {
        updatedPresentation.appliedRules = extractAppliedRulesFromSlides(presentation.slides);
    }

    if (returnSlides) {
        updatedPresentation.slides = presentation.slides.map((slide) => mapOldSlideToNewSlide(slide));
        return updatedPresentation;
    }

    return {...updatedPresentation, slides: []};
};

export const mapOldSlideToNewSlide = (slide: ISlide): ISlide => {
    const updatedElements = slide.elements.map((element) => mapOldElementToNewElement(element));

    return {
        ...slide,
        elements: updatedElements,
    };
};

export const mapOldElementToNewElement = (element: IElement): IElement => ({
    ...element,
});

export const extractAppliedRulesFromSlides = (slides: ISlide[] | undefined): IAppliedRule[] => {
    return (
        slides?.reduce((appliedRules: IAppliedRule[], slide): IAppliedRule[] => {
            slide.appliedRules?.forEach((rule) => {
                if (arrIncludesRule(appliedRules, rule)) {
                    appliedRules.forEach((accRule) => {
                        if (isSameRule(accRule, rule)) {
                            accRule.results[0].slideIds.push(String(slide._id));
                        }
                    });
                } else {
                    const newAppliedRule = mapOldAppliedRuleToNewAppliedRule(rule);
                    appliedRules.push({...newAppliedRule, results: [{...newAppliedRule.results[0], slideIds: [String(slide._id)]}]});
                }
            });
            return appliedRules;
        }, [] as IAppliedRule[]) || []
    );
};

export const mapOldAppliedRuleToNewAppliedRule = (appliedRule: IOldAppliedRule): IAppliedRule => ({
    predicates: [
        {
            elementType: <ELEMENT>appliedRule.predicate.elementType,
            elementId: appliedRule.predicate.elementId,
            answers: getRuleAnswers(appliedRule.predicate),
            operator: typeof appliedRule.operator === 'object' ? getRuleOperator(appliedRule.operator) : appliedRule.operator,
        },
    ],
    results: [
        {
            resultType: getRuleResult(appliedRule.result),
            slideIds: [],
        },
    ],
    name: appliedRule.predicate.name,
});

const getRuleOperator = (operator: {label: string; value: string}): RuleOperator => {
    if (operator.value === 'if not') {
        return RuleOperator.IF_NOT;
    }
    return RuleOperator.IF;
};

const getRuleResult = (result: {label: string; value: string}): RuleResultType => {
    if (result.value === 'show') {
        return RuleResultType.SHOW_SLIDES;
    }
    if (result.value === 'hide') {
        return RuleResultType.HIDE_SLIDES;
    }
    if (result.value === 'block') {
        return RuleResultType.BLOCK_SLIDE;
    }
    return RuleResultType.SHOW_SLIDES;
};

const getRuleAnswers = (predicate: IOldPredicate): number[] => {
    if (typeof predicate.answer === 'object') {
        return [predicate.answer.answerIndex];
    }
    if (predicate.predicateType === 'answeredAny') {
        return [0];
    }
    return [predicate.answer];
};

const isSameRule = (newRule: IAppliedRule, oldRule: IOldAppliedRule): boolean => {
    const predicate1 = newRule.predicates[0];
    const result1 = newRule.results[0];
    const isSameElementId = predicate1.elementId === oldRule.predicate.elementId;
    const isSameAnswer =
        predicate1.answers !== undefined &&
        oldRule.predicate.answer !== undefined &&
        predicate1.answers.includes(oldRule.predicate.answer as number);
    const isSameResult = result1.resultType === oldRule.result.value;
    const isSameOperator = predicate1.operator === oldRule.operator.value;

    return isSameElementId && isSameAnswer && isSameResult && isSameOperator;
};

const arrIncludesRule = (arr: IAppliedRule[], rule: IOldAppliedRule): boolean => {
    return arr.some((r) => isSameRule(r, rule));
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const mapOldLanguageConfigToNew = (
    languageConfig: {playerLabels?: any; labels: PlayerLabel[]} | undefined
): {labels: PlayerLabel[]} | undefined => {
    if (!languageConfig || (languageConfig && languageConfig.labels)) {
        return languageConfig;
    }
    const labels = Object.keys(languageConfig.playerLabels).map((key) => {
        return {
            _id: key,
            code: key,
            hr: languageConfig.playerLabels[key],
            en: languageConfig.playerLabels[key],
        };
    });

    return {labels};
};
